import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { setSentryUser } from "./sentry";
import ErrorBoundary from "./components/ErrorBoundary";
import { motion } from "framer-motion"; // eslint-disable-line
import {
  Shield,
  Zap,
  Globe,
  Heart,
  Camera,
  Database,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Brain,
  Cpu,
  Eye,
  Fingerprint,
  Layers,
  Network,
  Scan,
  PawPrint,
  Search,
  Plus,
  UserCheck,
  LogIn,
  User,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  MapPin,
  Smartphone,
  FileText,
  Upload,
} from "lucide-react";

import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import PetProfile from "./components/PetProfile";
import TailwindTest from "./TailwindTest";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import EnhancedModal from "./components/EnhancedModal";

// Theme Toggle Component for Main Page
function ThemeToggle() {
  const { theme, toggleTheme, themeClasses } = useTheme();

  const getThemeIcon = () => {
    if (theme === "dark") return <Moon className="w-4 h-4" />;
    if (theme === "light") return <Sun className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const getThemeLabel = () => {
    if (theme === "dark") return "Dark";
    if (theme === "light") return "Light";
    return "Auto";
  };

  return (
    <button
      onClick={toggleTheme}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${themeClasses.bg.buttonSecondary} border ${themeClasses.border.primary} hover:${themeClasses.border.accent} ${themeClasses.text.secondary} hover:${themeClasses.text.accent}`}
      title={`Current theme: ${getThemeLabel()}. Click to cycle through themes.`}
    >
      {getThemeIcon()}
      <span className="text-sm font-medium hidden sm:inline">
        {getThemeLabel()}
      </span>
    </button>
  );
}

// Main App Component (existing homepage)
function MainApp({ user, showAuth, setShowAuth, authLoading }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'found-pet' or 'register-pet'
    step: "auth", // 'auth', 'task', 'success'
  });
  const { themeClasses } = useTheme();

  // Handle modal flow
  const openModal = (mode) => {
    setModalState({
      isOpen: true,
      mode: mode,
      step: "auth",
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      mode: null,
      step: "auth",
    });
  };

  const nextStep = () => {
    setModalState((prev) => ({
      ...prev,
      step: prev.step === "auth" ? "task" : "success",
    }));
  };

  // If user is logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div
        className={`min-h-screen ${themeClasses.bg.primary} flex items-center justify-center`}
      >
        <div className="text-center">
          <div
            className={`animate-spin w-12 h-12 border-2 ${themeClasses.border.accent} border-t-transparent rounded-full mx-auto mb-4`}
          ></div>
          <p className={themeClasses.text.secondary}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${themeClasses.bg.primary} ${themeClasses.text.primary}`}
    >
      {/* Hidden component to force Tailwind classes */}
      <TailwindTest />

      {/* Auth Modal */}
      {showAuth && <Auth onClose={() => setShowAuth(false)} />}

      {/* Enhanced Modal System */}
      {modalState.isOpen && (
        <EnhancedModal
          modalState={modalState}
          onClose={closeModal}
          onNextStep={nextStep}
          user={user}
        />
      )}

      {/* Navigation */}
      <nav
        className={`${themeClasses.bg.primary} border-b ${themeClasses.border.primary} sticky top-0 z-50 backdrop-blur-md bg-opacity-80`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <PawPrint className={`h-6 w-6 ${themeClasses.text.accent}`} />
              <span
                className={`ml-2 text-xl font-medium ${themeClasses.text.primary}`}
              >
                PetID.AI
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#features"
                className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} text-sm font-medium transition-colors duration-200`}
              >
                Features
              </a>
              <a
                href="#pricing"
                className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} text-sm font-medium transition-colors duration-200`}
              >
                Pricing
              </a>
              <ThemeToggle />
              <button
                onClick={() => setShowAuth(true)}
                className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} text-sm font-medium transition-colors duration-200`}
              >
                Sign In
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} p-2 rounded-lg transition-colors`}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div
                className={`px-4 pt-2 pb-3 space-y-1 ${themeClasses.bg.primary} border-t ${themeClasses.border.primary}`}
              >
                <a
                  href="#features"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} block py-2 text-base font-medium transition-colors duration-200`}
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} block py-2 text-base font-medium transition-colors duration-200`}
                >
                  Pricing
                </a>
                <div
                  className={`pt-4 pb-3 border-t ${themeClasses.border.primary}`}
                >
                  <div className="flex flex-col space-y-3">
                    <div className="flex justify-center mb-3">
                      <ThemeToggle />
                    </div>
                    <button
                      onClick={() => {
                        setShowAuth(true);
                        setMobileMenuOpen(false);
                      }}
                      className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} text-base font-medium transition-colors duration-200 text-center py-2`}
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className={`relative min-h-screen flex items-center overflow-hidden ${themeClasses.bg.primary}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Main Heading */}
              <motion.h1
                className={`text-5xl sm:text-6xl md:text-7xl font-semibold mb-6 leading-tight ${themeClasses.text.primary}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                AI Pet Recognition
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className={`text-xl md:text-2xl ${themeClasses.text.secondary} mb-12 max-w-3xl leading-relaxed font-light`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Instantly identify pets using advanced AI technology. Reunite
                lost animals with their families.
              </motion.p>

              {/* Action Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <motion.button
                  onClick={() => openModal("found-pet")}
                  className="btn-primary text-lg px-8 py-4 font-medium flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Search className="w-5 h-5" />
                  <span>I Found a Pet</span>
                </motion.button>
                <motion.button
                  onClick={() => openModal("register-pet")}
                  className="btn-secondary text-lg px-8 py-4 font-medium flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Register My Pet</span>
                </motion.button>
              </motion.div>
            </div>

            {/* Right Visual */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <motion.div
                className="relative overflow-hidden rounded-3xl shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1583337130417-3346a1be7dee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Happy golden retriever and tabby cat sitting together"
                  className="w-full h-96 object-cover"
                />
                <div
                  className={`absolute inset-0 ${themeClasses.bg.primary} bg-opacity-10`}
                ></div>
                <motion.div
                  className="absolute bottom-6 left-6 right-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                >
                  <div
                    className={`${themeClasses.bg.card} bg-opacity-95 backdrop-blur-md rounded-2xl p-6 border ${themeClasses.border.primary}`}
                  >
                    <div className="flex items-center space-x-4">
                      <motion.div
                        className={`w-12 h-12 ${themeClasses.bg.accent} bg-opacity-20 rounded-full flex items-center justify-center`}
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <Brain
                          className={`w-6 h-6 ${themeClasses.text.accent}`}
                        />
                      </motion.div>
                      <div>
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          AI Identification Complete
                        </h4>
                        <p className={`text-sm ${themeClasses.text.secondary}`}>
                          Golden Retriever • 99.8% confidence
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        className={`py-32 ${themeClasses.bg.secondary} relative overflow-hidden`}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-32">
            <h2
              className={`text-5xl md:text-7xl font-bold ${themeClasses.text.primary} mb-8 tracking-tight`}
            >
              How It Works
            </h2>
            <p
              className={`text-2xl ${themeClasses.text.secondary} max-w-3xl mx-auto font-light leading-relaxed`}
            >
              Three simple steps to reunite pets with their families
            </p>
          </div>

          <div className="relative">
            {/* Enhanced Curved Flow Line */}
            <div className="hidden lg:block absolute top-0 left-0 right-0 h-full pointer-events-none">
              <svg
                className="w-full h-full"
                viewBox="0 0 1200 400"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="flowGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stopColor="rgb(59, 130, 246)"
                      stopOpacity="0.1"
                    />
                    <stop
                      offset="50%"
                      stopColor="rgb(59, 130, 246)"
                      stopOpacity="0.4"
                    />
                    <stop
                      offset="100%"
                      stopColor="rgb(59, 130, 246)"
                      stopOpacity="0.1"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M 100 200 Q 400 150 600 200 Q 800 250 1100 200"
                  stroke="url(#flowGradient)"
                  strokeWidth="3"
                  fill="none"
                  className="animate-pulse"
                />
              </svg>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 relative z-20">
              {/* Step 1 - Capture */}
              <motion.div
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                {/* Step Number with Animation */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-40">
                  <div className="relative">
                    <div
                      className={`w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-900 dark:text-gray-100 text-3xl font-black shadow-2xl border-2 border-gray-200 dark:border-gray-700 group-hover:scale-110 transition-all duration-500`}
                    >
                      1
                    </div>
                    {/* Pulse Ring */}
                    <div
                      className={`absolute inset-0 w-20 h-20 bg-gray-300 dark:bg-gray-600 rounded-full opacity-20 animate-ping`}
                    ></div>
                  </div>
                </div>

                {/* Main Card */}
                <div
                  className={`${themeClasses.bg.card} rounded-3xl p-10 pt-16 shadow-2xl border ${themeClasses.border.primary} group-hover:shadow-3xl transition-all duration-700 group-hover:scale-[1.05] group-hover:-translate-y-2 relative overflow-hidden h-[600px] flex flex-col`}
                >
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                  <div className="text-center relative z-10 flex-1 flex flex-col">
                    {/* Enhanced Icon */}
                    <div
                      className={`w-28 h-28 ${themeClasses.bg.accent} bg-opacity-10 rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-xl`}
                    >
                      <Camera
                        className={`w-14 h-14 ${themeClasses.text.accent}`}
                      />
                    </div>

                    {/* Content */}
                    <h3
                      className={`text-4xl font-bold ${themeClasses.text.primary} mb-6 group-hover:text-blue-600 transition-colors duration-300`}
                    >
                      Capture
                    </h3>
                    <p
                      className={`${themeClasses.text.secondary} text-xl leading-relaxed mb-8 max-w-sm mx-auto flex-1`}
                    >
                      Simply take a photo of your pet's face using your phone or
                      camera
                    </p>

                    {/* Enhanced Demo Image */}
                    <div className="relative overflow-hidden rounded-3xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-500 mt-auto">
                      <img
                        src="https://images.unsplash.com/photo-1552053831-71594a27632d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                        alt="Person taking photo of a golden retriever"
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                      {/* Camera UI Overlay */}
                      <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div
                          className={`w-16 h-16 ${themeClasses.bg.card} bg-opacity-95 rounded-full flex items-center justify-center shadow-2xl backdrop-blur-sm`}
                        >
                          <Camera
                            className={`w-8 h-8 ${themeClasses.text.accent}`}
                          />
                        </div>
                      </div>
                      {/* Focus Frame */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-white rounded-lg animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 2 - Analyze */}
              <motion.div
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                {/* Step Number with Animation */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-40">
                  <div className="relative">
                    <div
                      className={`w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-900 dark:text-gray-100 text-3xl font-black shadow-2xl border-2 border-gray-200 dark:border-gray-700 group-hover:scale-110 transition-all duration-500`}
                    >
                      2
                    </div>
                    {/* Pulse Ring */}
                    <div
                      className={`absolute inset-0 w-20 h-20 bg-gray-300 dark:bg-gray-600 rounded-full opacity-20 animate-ping`}
                      style={{ animationDelay: "0.5s" }}
                    ></div>
                  </div>
                </div>

                {/* Main Card */}
                <div
                  className={`${themeClasses.bg.card} rounded-3xl p-10 pt-16 shadow-2xl border ${themeClasses.border.primary} group-hover:shadow-3xl transition-all duration-700 group-hover:scale-[1.05] group-hover:-translate-y-2 relative overflow-hidden h-[600px] flex flex-col`}
                >
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                  <div className="text-center relative z-10 flex-1 flex flex-col">
                    {/* Enhanced Icon */}
                    <div
                      className={`w-28 h-28 ${themeClasses.bg.accent} bg-opacity-10 rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-xl`}
                    >
                      <Brain
                        className={`w-14 h-14 ${themeClasses.text.accent}`}
                      />
                    </div>

                    {/* Content */}
                    <h3
                      className={`text-4xl font-bold ${themeClasses.text.primary} mb-6 group-hover:text-purple-600 transition-colors duration-300`}
                    >
                      Analyze
                    </h3>
                    <p
                      className={`${themeClasses.text.secondary} text-xl leading-relaxed mb-8 max-w-sm mx-auto flex-1`}
                    >
                      Our AI instantly analyzes facial features, patterns, and
                      unique markers
                    </p>

                    {/* Enhanced AI Visualization */}
                    <div className="relative overflow-hidden rounded-3xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-500 mt-auto">
                      <img
                        src="https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                        alt="AI technology analyzing data with neural networks"
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>

                      {/* AI Analysis Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div
                          className={`w-24 h-24 border-4 ${themeClasses.border.accent} rounded-full animate-spin`}
                          style={{ animationDuration: "3s" }}
                        ></div>
                        <div
                          className={`absolute w-16 h-16 ${themeClasses.bg.accent} rounded-full flex items-center justify-center shadow-2xl`}
                        >
                          <Brain className="w-8 h-8 text-white animate-pulse" />
                        </div>
                      </div>

                      {/* Enhanced Progress Indicator */}
                      <div className="absolute bottom-6 left-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div
                          className={`${themeClasses.bg.card} bg-opacity-95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                              <div
                                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0.1s" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0.2s" }}
                              ></div>
                            </div>
                            <span
                              className={`text-sm ${themeClasses.text.primary} font-semibold`}
                            >
                              Analyzing features...
                            </span>
                          </div>
                          {/* Progress Bar */}
                          <div
                            className={`mt-2 h-1 ${themeClasses.bg.secondary} rounded-full overflow-hidden`}
                          >
                            <div
                              className="h-full bg-blue-500 rounded-full animate-pulse"
                              style={{ width: "75%" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 3 - Connect */}
              <motion.div
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                {/* Step Number with Animation */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-40">
                  <div className="relative">
                    <div
                      className={`w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-900 dark:text-gray-100 text-3xl font-black shadow-2xl border-2 border-gray-200 dark:border-gray-700 group-hover:scale-110 transition-all duration-500`}
                    >
                      3
                    </div>
                    {/* Pulse Ring */}
                    <div
                      className={`absolute inset-0 w-20 h-20 bg-gray-300 dark:bg-gray-600 rounded-full opacity-20 animate-ping`}
                      style={{ animationDelay: "1s" }}
                    ></div>
                  </div>
                </div>

                {/* Main Card */}
                <div
                  className={`${themeClasses.bg.card} rounded-3xl p-10 pt-16 shadow-2xl border ${themeClasses.border.primary} group-hover:shadow-3xl transition-all duration-700 group-hover:scale-[1.05] group-hover:-translate-y-2 relative overflow-hidden h-[600px] flex flex-col`}
                >
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                  <div className="text-center relative z-10 flex-1 flex flex-col">
                    {/* Enhanced Icon */}
                    <div
                      className={`w-28 h-28 ${themeClasses.bg.accent} bg-opacity-10 rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-xl`}
                    >
                      <UserCheck
                        className={`w-14 h-14 ${themeClasses.text.accent}`}
                      />
                    </div>

                    {/* Content */}
                    <h3
                      className={`text-4xl font-bold ${themeClasses.text.primary} mb-6 group-hover:text-green-600 transition-colors duration-300`}
                    >
                      Connect
                    </h3>
                    <p
                      className={`${themeClasses.text.secondary} text-xl leading-relaxed mb-8 max-w-sm mx-auto flex-1`}
                    >
                      Get instant results with owner details and seamless
                      reunion coordination
                    </p>

                    {/* Enhanced Success Result */}
                    <div className="relative overflow-hidden rounded-3xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-500 mt-auto">
                      <img
                        src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                        alt="Happy family with their beloved pet dog reunited"
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>

                      {/* Success Notification */}
                      <div className="absolute top-6 left-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div
                          className={`${themeClasses.bg.card} bg-opacity-95 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border border-green-200 dark:border-green-800`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
                              <div className="absolute inset-0 w-4 h-4 bg-green-500 rounded-full animate-ping opacity-75"></div>
                            </div>
                            <div className="flex-1">
                              <div
                                className={`text-base ${themeClasses.text.primary} font-bold mb-1`}
                              >
                                🎉 Match Found!
                              </div>
                              <div
                                className={`text-sm ${themeClasses.text.secondary} flex items-center space-x-2`}
                              >
                                <span>Owner contacted</span>
                                <span>•</span>
                                <span className="font-semibold">2.1s</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Our App Section */}
      <section
        className={`py-32 ${themeClasses.bg.primary} relative overflow-hidden`}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Content */}
            <motion.div
              className="text-center lg:text-left"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.h2
                className={`text-4xl md:text-6xl font-bold ${themeClasses.text.primary} mb-8 tracking-tight`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                Take PetID.AI
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">
                  Everywhere
                </span>
              </motion.h2>

              <motion.p
                className={`text-xl ${themeClasses.text.secondary} mb-12 leading-relaxed max-w-lg lg:max-w-none`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Access all features on the go with our mobile app. Identify pets
                instantly, manage health records, and coordinate reunions from
                anywhere.
              </motion.p>

              {/* Features List */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 ${themeClasses.bg.accent} rounded-full flex items-center justify-center flex-shrink-0`}
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className={`${themeClasses.text.primary} font-medium`}>
                    Instant Pet Identification
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 ${themeClasses.bg.accent} rounded-full flex items-center justify-center flex-shrink-0`}
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className={`${themeClasses.text.primary} font-medium`}>
                    Health Record Management
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 ${themeClasses.bg.accent} rounded-full flex items-center justify-center flex-shrink-0`}
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className={`${themeClasses.text.primary} font-medium`}>
                    Digital Vaccination Passport
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 ${themeClasses.bg.accent} rounded-full flex items-center justify-center flex-shrink-0`}
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className={`${themeClasses.text.primary} font-medium`}>
                    Offline Functionality
                  </span>
                </div>
              </motion.div>

              {/* Download Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                {/* App Store Button */}
                <a
                  href="#"
                  className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-2xl px-6 py-4 flex items-center space-x-4 hover:shadow-xl transition-all duration-300 group`}
                >
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className={`text-sm ${themeClasses.text.secondary}`}>
                      Download on the
                    </div>
                    <div
                      className={`text-lg font-semibold ${themeClasses.text.primary} group-hover:text-blue-600 transition-colors`}
                    >
                      App Store
                    </div>
                  </div>
                </a>

                {/* Google Play Button */}
                <a
                  href="#"
                  className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-2xl px-6 py-4 flex items-center space-x-4 hover:shadow-xl transition-all duration-300 group`}
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className={`text-sm ${themeClasses.text.secondary}`}>
                      Get it on
                    </div>
                    <div
                      className={`text-lg font-semibold ${themeClasses.text.primary} group-hover:text-green-600 transition-colors`}
                    >
                      Google Play
                    </div>
                  </div>
                </a>
              </motion.div>
            </motion.div>

            {/* Right Side - iPhone 14 Pro Screenshot */}
            <motion.div
              className="flex justify-center lg:justify-end"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="relative">
                {/* iPhone 14 Pro Frame with Screenshot */}
                <div className="relative w-80 h-[692px] bg-black rounded-[3.5rem] p-2 shadow-2xl">
                  {/* iPhone 14 Pro Screen */}
                  <div className="w-full h-full bg-black rounded-[2.8rem] overflow-hidden relative">
                    {/* Dynamic Island */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-32 h-8 bg-black rounded-full z-10 shadow-inner"></div>

                    {/* Screen Content */}
                    <img
                      src="/app-screenshot.png"
                      alt="PetID.AI Mobile App Interface showing Quick Actions and user dashboard"
                      className="w-full h-full object-cover rounded-[2.8rem]"
                    />
                  </div>

                  {/* iPhone 14 Pro Side Buttons */}
                  <div className="absolute left-[-3px] top-20 w-1 h-12 bg-black rounded-r-sm"></div>
                  <div className="absolute left-[-3px] top-36 w-1 h-16 bg-black rounded-r-sm"></div>
                  <div className="absolute left-[-3px] top-56 w-1 h-16 bg-black rounded-r-sm"></div>
                  <div className="absolute right-[-3px] top-48 w-1 h-20 bg-black rounded-l-sm"></div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-blue-500 rounded-full animate-bounce opacity-20"></div>
                <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-purple-500 rounded-full animate-pulse opacity-30"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className={`py-32 ${themeClasses.bg.secondary} relative`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className={`text-4xl md:text-5xl font-semibold ${themeClasses.text.primary} mb-6`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Powerful Features
            </motion.h2>
            <motion.p
              className={`text-xl ${themeClasses.text.secondary} max-w-2xl mx-auto font-light`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Everything you need to keep your pets safe and connected.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Camera,
                title: "AI Recognition",
                description:
                  "Instantly identify pets using advanced neural networks and facial recognition technology.",
              },
              {
                icon: Shield,
                title: "Digital Profiles",
                description:
                  "Create secure digital identities with photos, medical records, and emergency contacts.",
              },
              {
                icon: Search,
                title: "Lost Pet Recovery",
                description:
                  "Connect found pets with their families through our global network of shelters and vets.",
              },
              {
                icon: Heart,
                title: "Health Tracking",
                description:
                  "Manage vaccination records, medical history, and appointment reminders in one place.",
              },
              {
                icon: MapPin,
                title: "Location Services",
                description:
                  "Advanced mapping and GPS features for tracking and coordinating search efforts.",
              },
              {
                icon: Smartphone,
                title: "Mobile & API",
                description:
                  "Full-featured mobile apps and comprehensive API for seamless integration.",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                viewport={{ once: true, margin: "-50px" }}
              >
                <motion.div
                  className={`w-16 h-16 ${themeClasses.bg.button} rounded-2xl flex items-center justify-center mx-auto mb-6`}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <feature.icon className="h-8 w-8 text-white" />
                </motion.div>
                <h3
                  className={`text-xl font-medium ${themeClasses.text.primary} mb-3`}
                >
                  {feature.title}
                </h3>
                <p className={`${themeClasses.text.secondary} leading-relaxed`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className={`py-32 ${themeClasses.bg.primary}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2
              className={`text-4xl md:text-5xl font-semibold ${themeClasses.text.primary} mb-6`}
            >
              Reuniting Families
            </h2>
            <p
              className={`text-xl ${themeClasses.text.secondary} max-w-2xl mx-auto font-light`}
            >
              Every day, our AI helps bring lost pets back home to their
              families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group">
              <div className="overflow-hidden rounded-2xl shadow-lg mb-6 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="https://images.unsplash.com/photo-1583337130417-3346a1be7dee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                  alt="Happy golden retriever with family"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="text-center">
                <h3
                  className={`text-xl font-medium ${themeClasses.text.primary} mb-3`}
                >
                  Max Found After 3 Days
                </h3>
                <p className={`${themeClasses.text.secondary} leading-relaxed`}>
                  "Our golden retriever went missing during a camping trip.
                  Within hours of posting his photo, a hiker 50 miles away
                  identified him!"
                </p>
                <p
                  className={`${themeClasses.text.accent} text-sm font-medium mt-2`}
                >
                  - Sarah & Mike, Portland
                </p>
              </div>
            </div>

            <div className="group">
              <div className="overflow-hidden rounded-2xl shadow-lg mb-6 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="https://images.unsplash.com/photo-1574144611937-0df059b5ef3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                  alt="Happy family with their rescued cat"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="text-center">
                <h3
                  className={`text-xl font-medium ${themeClasses.text.primary} mb-3`}
                >
                  Luna's Miraculous Return
                </h3>
                <p className={`${themeClasses.text.secondary} leading-relaxed`}>
                  "After 2 weeks of searching, a vet clinic scanned Luna and
                  instantly found her profile. She's home safe and sound!"
                </p>
                <p
                  className={`${themeClasses.text.accent} text-sm font-medium mt-2`}
                >
                  - Jennifer, Austin
                </p>
              </div>
            </div>

            <div className="group">
              <div className="overflow-hidden rounded-2xl shadow-lg mb-6 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="https://images.unsplash.com/photo-1601758228041-f3b2795255f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
                  alt="Happy dog with children"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="text-center">
                <h3
                  className={`text-xl font-medium ${themeClasses.text.primary} mb-3`}
                >
                  Buddy's Quick Rescue
                </h3>
                <p className={`${themeClasses.text.secondary} leading-relaxed`}>
                  "Someone found Buddy and used the app to identify him in
                  seconds. He was back with our kids within an hour!"
                </p>
                <p
                  className={`${themeClasses.text.accent} text-sm font-medium mt-2`}
                >
                  - The Rodriguez Family, Miami
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <div
              className={`inline-flex items-center px-6 py-3 ${themeClasses.bg.accent} bg-opacity-10 rounded-full`}
            >
              <Heart className={`w-5 h-5 ${themeClasses.text.accent} mr-2`} />
              <span className={`${themeClasses.text.accent} font-medium`}>
                Over 10,000 successful reunions and counting
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className={`py-32 ${themeClasses.bg.secondary} relative`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className={`text-4xl md:text-5xl font-semibold ${themeClasses.text.primary} mb-6`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Choose Your Plan
            </motion.h2>
            <motion.p
              className={`text-xl ${themeClasses.text.secondary} max-w-2xl mx-auto font-light`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Start free and scale as you grow. Professional-grade AI for every
              need.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Pet Owner",
                price: "Free",
                unit: "up to 10 scans/month",
                description: "Perfect for individual pet owners",
                features: [
                  "Register up to 3 pets",
                  "Unlimited pet lookups",
                  "Basic health records",
                  "Lost pet alerts",
                  "Mobile app access",
                ],
                popular: false,
                color: "cyan",
              },
              {
                name: "Professional",
                price: "$4.99",
                unit: "per API call",
                description: "For vets, shelters, and pet businesses",
                features: [
                  "Unlimited pet registrations",
                  "Advanced health tracking",
                  "API access & webhooks",
                  "Priority support",
                  "Custom branding",
                  "Bulk scanning tools",
                ],
                popular: true,
                color: "purple",
              },
              {
                name: "Enterprise",
                price: "Custom",
                unit: "volume pricing",
                description: "For large organizations and government agencies",
                features: [
                  "Unlimited API usage",
                  "Custom model training",
                  "Dedicated infrastructure",
                  "White-label solution",
                  "24/7 support",
                  "On-premise deployment",
                ],
                popular: false,
                color: "yellow",
              },
            ].map((plan, index) => (
              <motion.div
                key={index}
                className={`${themeClasses.bg.card} border ${
                  themeClasses.border.primary
                } rounded-2xl p-8 relative group ${
                  plan.popular
                    ? "ring-2 ring-blue-500/20 shadow-2xl"
                    : "shadow-lg"
                } hover:shadow-2xl transition-all duration-300`}
                initial={{
                  opacity: 0,
                  y: 50,
                  scale: plan.popular ? 0.9 : 1,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  duration: 0.6,
                  delay: 0.5 + index * 0.15,
                  type: plan.popular ? "spring" : "tween",
                  bounce: plan.popular ? 0.4 : 0,
                }}
                viewport={{ once: true, margin: "-50px" }}
              >
                {plan.popular && (
                  <motion.div
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                    initial={{ opacity: 0, scale: 0, y: 10 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.15 }}
                    viewport={{ once: true }}
                  >
                    <div
                      className={`${themeClasses.bg.accent} text-white px-4 py-2 rounded-full text-sm font-medium`}
                    >
                      Most Popular
                    </div>
                  </motion.div>
                )}

                <div className="text-center mb-8">
                  <h3
                    className={`text-xl font-semibold ${themeClasses.text.primary} mb-4`}
                  >
                    {plan.name}
                  </h3>
                  <div className="mb-4">
                    <span
                      className={`text-3xl font-semibold ${themeClasses.text.primary}`}
                    >
                      {plan.price}
                    </span>
                    <span
                      className={`${themeClasses.text.secondary} text-sm ml-1`}
                    >
                      {plan.unit}
                    </span>
                  </div>
                  <p className={`${themeClasses.text.secondary} text-sm`}>
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li
                      key={i}
                      className={`flex items-center ${themeClasses.text.secondary}`}
                    >
                      <CheckCircle
                        className={`h-5 w-5 ${themeClasses.text.accent} mr-3 flex-shrink-0`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full ${
                    plan.popular
                      ? `${themeClasses.bg.button} text-white`
                      : `${themeClasses.bg.primary} ${themeClasses.text.primary} border ${themeClasses.border.primary}`
                  } px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-[1.02] flex items-center justify-center`}
                >
                  {plan.price === "Custom"
                    ? "Contact Sales"
                    : plan.price === "Free"
                    ? "Start Free"
                    : "Get Started"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className={`py-32 ${themeClasses.bg.primary}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className={`text-4xl md:text-5xl font-semibold ${themeClasses.text.primary} mb-6 leading-tight`}
          >
            Keep Your Pets Safe
          </h2>
          <p
            className={`text-xl ${themeClasses.text.secondary} mb-12 max-w-2xl mx-auto font-light leading-relaxed`}
          >
            Join thousands of pet owners who trust our AI to protect their
            beloved companions.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowAuth(true)}
              className={`${themeClasses.bg.button} text-white px-8 py-4 rounded-xl text-lg font-medium transition-all duration-200 hover:scale-[1.02]`}
            >
              Get Started Free
            </button>
            <button
              onClick={() => setShowAuth(true)}
              className={`${themeClasses.bg.secondary} ${themeClasses.text.primary} border ${themeClasses.border.primary} px-8 py-4 rounded-xl text-lg font-medium transition-all duration-200 hover:scale-[1.02]`}
            >
              View Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className={`${themeClasses.bg.secondary} border-t ${themeClasses.border.primary}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <div className="relative">
                  <PawPrint
                    className={`h-8 w-8 ${themeClasses.text.accent} animate-pulse-glow`}
                  />
                  <div
                    className={`absolute inset-0 ${themeClasses.text.accent.replace(
                      "text-",
                      "bg-"
                    )} opacity-20 rounded-full blur-xl`}
                  ></div>
                </div>
                <span className="ml-3 text-2xl font-bold bg-gradient-to-r from-cyan-500 to-purple-600 bg-clip-text text-transparent">
                  PetID.AI
                </span>
              </div>
              <p className={`${themeClasses.text.secondary} mb-6 max-w-md`}>
                Advanced AI-powered pet identification platform. Instantly
                identify pets, reunite lost animals with families, and create
                secure digital profiles.
              </p>
              <div className="flex space-x-4">
                <div
                  className={`w-10 h-10 ${themeClasses.bg.tertiary} rounded-lg flex items-center justify-center border ${themeClasses.border.primary} hover:${themeClasses.border.accent} transition-colors cursor-pointer`}
                >
                  <span className={themeClasses.text.accent}>𝕏</span>
                </div>
                <div
                  className={`w-10 h-10 ${themeClasses.bg.tertiary} rounded-lg flex items-center justify-center border ${themeClasses.border.primary} hover:${themeClasses.border.accent} transition-colors cursor-pointer`}
                >
                  <span className={themeClasses.text.accent}>in</span>
                </div>
                <div
                  className={`w-10 h-10 ${themeClasses.bg.tertiary} rounded-lg flex items-center justify-center border ${themeClasses.border.primary} hover:${themeClasses.border.accent} transition-colors cursor-pointer`}
                >
                  <span className={themeClasses.text.accent}>gh</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-4`}>
                Platform
              </h4>
              <ul className={`space-y-2 ${themeClasses.text.secondary}`}>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    API Documentation
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Pet Recognition
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Lost Pet Recovery
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Health Records
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-4`}>
                Support
              </h4>
              <ul className={`space-y-2 ${themeClasses.text.secondary}`}>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Help Center
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Contact Support
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Status Page
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`hover:${themeClasses.text.accent} transition-colors`}
                  >
                    Community
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div
            className={`border-t ${themeClasses.border.primary} mt-12 pt-8 flex flex-col md:flex-row justify-between items-center`}
          >
            <p className={`${themeClasses.text.tertiary} text-sm`}>
              © 2024 PetID.AI. Advanced pet identification technology.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a
                href="#"
                className={`${themeClasses.text.tertiary} hover:${themeClasses.text.accent} text-sm transition-colors`}
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className={`${themeClasses.text.tertiary} hover:${themeClasses.text.accent} text-sm transition-colors`}
              >
                Terms of Service
              </a>
              <a
                href="#"
                className={`${themeClasses.text.tertiary} hover:${themeClasses.text.accent} text-sm transition-colors`}
              >
                Security
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Root App Component with Router
function App() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
      if (user) {
        setSentryUser(user);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Router>
          <Routes>
            <Route
              path="/"
              element={
                <MainApp
                  user={user}
                  showAuth={showAuth}
                  setShowAuth={setShowAuth}
                  authLoading={authLoading}
                />
              }
            />
            <Route path="/pet/:petId" element={<PetProfile />} />
            {/* Dashboard routes */}
            <Route
              path="/dashboard/*"
              element={
                user ? (
                  <Dashboard user={user} />
                ) : (
                  <MainApp
                    user={user}
                    showAuth={showAuth}
                    setShowAuth={setShowAuth}
                    authLoading={authLoading}
                  />
                )
              }
            />
          </Routes>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
